import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import Autocomplete from 'ui/Autocomplete';
import { getValueByPath } from 'utils/struct';

/**
 * Компонент-обертка для интеграции компонента Autocomplete с библиотекой react-hook-form.
 * Упрощает использование Autocomplete в формах, управляемых через react-hook-form.
 *
 * @param {Object[]} items - Элементы выпадающего списка
 * @param {string} [keyItem='id'] - Имя поля в объекте options, которое используется как значение опции
 * @param {string} [valueItem='name'] - Имя поля в объекте options, которое используется как текст опции
 * @param {function} onChange - Функция для обработки ввода текста в input для поиска
 * @param {Object[]} preSelectedItems - Выбранные элементы списка по умолчанию
 * @param {string} preSelectedTitle - Текст для отображения, когда элемент выбран
 * @param {string} [placeholder='Не выбрано'] - Текст для отображения, когда ничего не выбрано
 * @param {number} [minValueForSearch=3] - Минимальная длина текста для поиска
 * @param {boolean} [disabled=false] - Флаг, определяющий, отключен ли компонент
 * @param {function} [renderOption] - Функция для кастомного рендеринга опций
 * @param {string} name - Имя поля в форме (обязательный параметр)
 * @param {Object} [rules] - Правила валидации для react-hook-form
 * @param {function} [onSelect] - Функция обработки выбора элемента
 * @param {boolean} hideErrorBlock - скрыть блок для ошибки
 * @param {boolean} [required=false] - Флаг указывающий на обязательность заполнения поля
 **/

export const FormAutocomplete = ({
  items,
  keyItem = 'id',
  valueItem = 'name',
  onChange,
  preSelectedItems = undefined,
  preSelectedTitle = 'Используется',
  placeholder = 'Введите название...',
  minValueForSearch = 3,
  disabled = false,
  renderOption = undefined,
  name,
  rules = {},
  onSelect = undefined,
  hideErrorBlock = false,
  required = false,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const formRules = useMemo(() => {
    let defaultRules = {};
    if (required) {
      defaultRules.required = 'Поле обязательно для заполнения';
    }

    return { ...defaultRules, ...rules };
  }, [required, rules]);

  return (
    <Controller
      name={name}
      control={control}
      rules={formRules}
      render={({ field }) => (
        <Autocomplete
          items={items}
          onChange={onChange}
          preSelectedItems={preSelectedItems}
          preSelectedTitle={preSelectedTitle}
          renderOption={renderOption}
          placeholder={placeholder}
          minValueForSearch={minValueForSearch}
          disabled={disabled}
          onSelect={(...rest) => {
            const [key, value] = rest;
            field.onChange(key);
            onSelect?.(key, value);
          }}
          keyItem={keyItem}
          valueItem={valueItem}
          isFormValidate={!!getValueByPath(errors, name)}
          errorMessage={getValueByPath(errors, name)}
          hideErrorBlock={hideErrorBlock}
          required={required}
        />
      )}
    />
  );
};

export default FormAutocomplete;
